/**
 * @fileoverview Unlock Duration Settings - User interface for configuring unlock expiry
 *
 * Allows users to select how long they want to stay unlocked before requiring re-authentication.
 * Includes warnings for less secure options.
 *
 * @module components/UnlockDurationSettings
 */

import { useState, useEffect } from 'react';
import { AlertCircle, Check } from 'lucide-react';
import { UNLOCK_DURATION_OPTIONS } from '../services/UnlockPreferencesService';
import {
  getUserPreferences,
  setUserPreferences,
} from '../services/UserPreferencesService';
import { useAuth } from '../contexts/AuthContext';
import { createLogger } from '@xanectech/lib';

const logger = createLogger('UnlockDurationSettings');

interface UnlockDurationSettingsProps {
  onClose?: () => void;
  compact?: boolean; // For embedded dialogs
}

export function UnlockDurationSettings({
  onClose,
  compact = false,
}: UnlockDurationSettingsProps) {
  const { user } = useAuth();
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  // Load current preference
  useEffect(() => {
    const loadPreference = async () => {
      if (!user) return;
      try {
        const preferences = await getUserPreferences(user.id);
        setSelected((preferences.expiry as string) || 'one-hour');
      } catch (error) {
        logger.error('Failed to load preference', error as Error);
        setSelected('one-hour'); // Default
      }
    };
    void loadPreference();
  }, [user]);

  const handleSelect = async (optionId: string) => {
    if (!user) return;

    setSelected(optionId);
    setLoading(true);
    setSaved(false);

    try {
      // Update preferences in database (via UserPreferencesService)
      await setUserPreferences(user.id, { expiry: optionId });

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      logger.info('Unlock duration preference saved', { optionId });
    } catch (error) {
      logger.error('Failed to save preference', error as Error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  const containerClass = compact
    ? 'space-y-3'
    : 'bg-card rounded-lg p-6 space-y-4';

  return (
    <div className={containerClass}>
      <div className={!compact ? 'mb-4' : ''}>
        <h3 className='text-lg font-semibold text-foreground'>
          🔓 Unlock Duration
        </h3>
        <p className='text-sm text-muted-foreground mt-1'>
          How long should you stay unlocked before requiring re-authentication?
        </p>
      </div>

      <div className='space-y-2'>
        {UNLOCK_DURATION_OPTIONS.map((option) => {
          const isSelected = selected === option.id;
          const bgColor =
            option.warningLevel === 'critical'
              ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
              : option.warningLevel === 'warning'
                ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                : 'bg-muted border-border';

          const selectedBgColor =
            option.warningLevel === 'critical'
              ? 'bg-red-100 dark:bg-red-900/40 border-red-300 dark:border-red-700'
              : option.warningLevel === 'warning'
                ? 'bg-yellow-100 dark:bg-yellow-900/40 border-yellow-300 dark:border-yellow-700'
                : 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700';

          return (
            <button
              key={option.id}
              onClick={() => handleSelect(option.id)}
              disabled={loading}
              className={`w-full text-left p-4 border-2 rounded-lg transition-all ${
                isSelected ? selectedBgColor : bgColor
              } ${
                loading
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer hover:border-opacity-75'
              }`}
            >
              <div className='flex items-start gap-3'>
                <div
                  className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    isSelected
                      ? 'bg-primary border-white'
                      : 'bg-transparent border-gray-700'
                  }`}
                >
                  {isSelected && <Check className='w-3 h-3 text-white' />}
                </div>
                <div className='flex-1'>
                  <div className='font-medium text-foreground'>
                    {option.label}
                  </div>
                  <div className='text-sm text-muted-foreground'>
                    {option.description}
                  </div>

                  {/* Critical warning */}
                  {option.warningLevel === 'critical' && (
                    <div className='mt-2 flex items-start gap-2 bg-red-100 dark:bg-red-900/50 p-2 rounded'>
                      <AlertCircle className='w-4 h-4 text-red-700 dark:text-red-300 flex-shrink-0 mt-0.5' />
                      <div className='text-xs text-red-700 dark:text-red-300'>
                        <strong>⚠️ CRITICAL:</strong> Only use on fully trusted
                        personal devices. Anyone with physical access can access
                        your encrypted data without authentication. Logout when
                        leaving the device.
                      </div>
                    </div>
                  )}

                  {/* Warning */}
                  {option.warningLevel === 'warning' && (
                    <div className='mt-2 flex items-start gap-2 bg-yellow-100 dark:bg-yellow-900/50 p-2 rounded'>
                      <AlertCircle className='w-4 h-4 text-warning flex-shrink-0 mt-0.5' />
                      <div className='text-xs text-warning'>
                        Risk if device is accessed by others. Consider shorter
                        duration on shared devices.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {saved && (
        <div className='bg-success/10 dark:bg-success/5 border border-success/30 rounded p-3'>
          <p className='text-sm text-green-700 dark:text-green-300'>
            ✅ Preference saved successfully
          </p>
        </div>
      )}

      {onClose && !compact && (
        <button
          onClick={onClose}
          className='w-full mt-4 px-4 py-2 text-foreground/90 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors'
        >
          Close
        </button>
      )}
    </div>
  );
}
